import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Switch,
  Textarea,
  TooltipProvider,
} from '@roger/ui-components';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { listPermissions, updateRole } from '../../services/api';
import type {
  PermissionResponse,
  RoleResponse,
} from '../../services/api/types.gen';
import type { RoleProduct } from '../../constants/products';
import RoleProductsPicker from './RoleProductsPicker';
import { PermissionBadge } from './PermissionBadge';
import { matchesPermissionSearch } from './permissionSearch';

type Props = {
  role: RoleResponse;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
};

const EditRoleDialog: React.FC<Props> = ({
  role,
  open,
  onClose,
  onUpdated,
}) => {
  const { showError, showSuccess, hasPermission } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
      hasPermission: state.hasPermission,
    }))
  );
  const canViewInternalRoles = hasPermission(
    'clinician-mgr:view-internal-roles'
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [products, setProducts] = useState<RoleProduct[]>([]);
  const [selectedPerms, setSelectedPerms] = useState<PermissionResponse[]>([]);
  const [allPermissions, setAllPermissions] = useState<PermissionResponse[]>(
    []
  );
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [permSearch, setPermSearch] = useState('');
  const [permDropdownOpen, setPermDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const permDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setName(role.name);
    setDescription(role.description ?? '');
    setIsInternal(role.is_internal ?? false);
    setProducts(role.products);
    setSelectedPerms(role.permissions ?? []);
    setPermSearch('');
    setPermDropdownOpen(false);
  }, [open, role]);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setIsLoadingPermissions(true);
      try {
        // Permissions define pure capabilities; any role can hold any permission
        const res = await listPermissions({
          query: { limit: 500 },
          throwOnError: true,
        });
        setAllPermissions(res.data ?? []);
      } catch (error) {
        console.error('Failed to load permissions:', error);
        showError('Failed to Load Permissions', 'Unable to load permissions.');
      } finally {
        setIsLoadingPermissions(false);
      }
    };
    load();
  }, [open, showError]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        permDropdownRef.current &&
        !permDropdownRef.current.contains(e.target as Node)
      ) {
        setPermDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredPerms = allPermissions.filter(p =>
    matchesPermissionSearch(p, permSearch)
  );

  const togglePermission = (perm: PermissionResponse) => {
    setSelectedPerms(prev =>
      prev.some(p => p.id === perm.id)
        ? prev.filter(p => p.id !== perm.id)
        : [...prev, perm]
    );
  };

  const removePermission = (permId: string) => {
    setSelectedPerms(prev => prev.filter(p => p.id !== permId));
  };

  // const isReadOnly = role.is_system;
  const isReadOnly = false; // TODO PEDRAM: it's read only if you are not a roger admin
  const isFormValid =
    name.trim() !== '' && description.trim() !== '' && products.length > 0;

  const handleUpdate = async () => {
    if (!isFormValid || isReadOnly) return;
    setIsSaving(true);
    try {
      await updateRole({
        path: { role_id: role.id },
        body: {
          name: name.trim(),
          description: description.trim(),
          is_internal: isInternal,
          products,
          permission_ids: selectedPerms.map(p => p.id),
        },
      });
      showSuccess(
        'Role Updated',
        `Role "${name.trim()}" was updated successfully.`
      );
      onUpdated();
      onClose();
    } catch (error) {
      console.error('Failed to update role:', error);
      showError(
        'Failed to Update Role',
        'Unable to update role. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className='max-w-lg max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Edit Role</DialogTitle>
        </DialogHeader>

        <div className='space-y-5 py-2'>
          {isReadOnly && (
            <div className='rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground'>
              System roles are read-only and cannot be modified.
            </div>
          )}

          {/* Name */}
          <div className='space-y-1.5'>
            <Label htmlFor='edit-role-name'>Name</Label>
            <Input
              id='edit-role-name'
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={isReadOnly}
            />
          </div>

          {/* Slug, Scope, Type — read-only, inline */}
          <div className='flex items-center gap-4 text-sm'>
            <div className='flex items-center gap-1.5'>
              <span className='text-muted-foreground'>Slug</span>
              <span className='font-mono text-xs'>{role.slug}</span>
            </div>
            <div className='flex items-center gap-1.5'>
              <span className='text-muted-foreground'>Scope</span>
              <Badge variant='outline' className='capitalize'>
                {role.scope.toLowerCase()}
              </Badge>
            </div>
            <div className='flex items-center gap-1.5'>
              <span className='text-muted-foreground'>Type</span>
              {role.is_system ? (
                <Badge variant='secondary'>System</Badge>
              ) : (
                <Badge variant='outline'>Custom</Badge>
              )}
            </div>
          </div>

          {/* Description */}
          <div className='space-y-1.5'>
            <Label htmlFor='edit-role-description'>Description</Label>
            <Textarea
              id='edit-role-description'
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              disabled={isReadOnly}
            />
          </div>

          {/* Internal Role */}
          {canViewInternalRoles && (
            <div className='flex items-center justify-between'>
              <div className='space-y-0.5'>
                <Label htmlFor='edit-role-internal'>Internal Role</Label>
                <p className='text-xs text-muted-foreground'>
                  Only visible to external admins in invite and edit flows
                </p>
              </div>
              <Switch
                id='edit-role-internal'
                checked={isInternal}
                onCheckedChange={setIsInternal}
                disabled={isReadOnly}
              />
            </div>
          )}

          <RoleProductsPicker
            idPrefix='edit-role-product'
            value={products}
            onChange={setProducts}
            disabled={isReadOnly}
          />
        </div>

        {/* Permissions */}
        <div className='space-y-1.5'>
          <Label>Permissions</Label>
          {isReadOnly ? (
            <div className='flex flex-wrap gap-1.5 pt-1'>
              {selectedPerms.length === 0 ? (
                <span className='text-sm text-muted-foreground italic'>
                  None
                </span>
              ) : (
                selectedPerms.map(perm => (
                  <Badge key={perm.id} variant='secondary'>
                    {perm.name}
                  </Badge>
                ))
              )}
            </div>
          ) : (
            <div className='relative' ref={permDropdownRef}>
              <Button
                variant='outline'
                className='w-full justify-between font-normal'
                type='button'
                disabled={isLoadingPermissions}
                onClick={() => setPermDropdownOpen(o => !o)}
              >
                <span className='text-sm text-muted-foreground truncate'>
                  {isLoadingPermissions
                    ? 'Loading permissions...'
                    : selectedPerms.length === 0
                      ? 'Select permissions...'
                      : `${selectedPerms.length} permission${selectedPerms.length !== 1 ? 's' : ''} selected`}
                </span>
                <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
              </Button>
              {permDropdownOpen && !isLoadingPermissions && (
                <div className='absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md'>
                  <div className='p-2 border-b'>
                    <Input
                      placeholder='Search permissions...'
                      value={permSearch}
                      onChange={e => setPermSearch(e.target.value)}
                      className='h-8 text-sm'
                      autoFocus
                    />
                  </div>
                  <div className='max-h-80 overflow-y-auto p-1'>
                    {filteredPerms.length === 0 ? (
                      <p className='py-2 text-center text-xs text-muted-foreground'>
                        {allPermissions.length === 0
                          ? 'No permissions available.'
                          : 'No permissions found.'}
                      </p>
                    ) : (
                      filteredPerms.map(perm => (
                        <div
                          key={perm.id}
                          className='flex w-full cursor-pointer items-start gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent'
                          onClick={() => togglePermission(perm)}
                        >
                          <Checkbox
                            checked={selectedPerms.some(s => s.id === perm.id)}
                            id={`edit-role-perm-${perm.id}`}
                            onCheckedChange={() => togglePermission(perm)}
                            onClick={e => e.stopPropagation()}
                          />
                          <div className='min-w-0 flex-1'>
                            <span className='font-medium leading-tight block'>
                              {perm.name}
                            </span>
                            {perm.description && (
                              <span className='block text-xs text-muted-foreground line-clamp-2 mt-0.5'>
                                {perm.description}
                              </span>
                            )}
                            <span className='block truncate text-[11px] font-mono text-muted-foreground/75 mt-0.5'>
                              {perm.slug}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              {selectedPerms.length > 0 && (
                <TooltipProvider delayDuration={150}>
                  <div className='flex flex-wrap gap-1.5 pt-2'>
                    {selectedPerms.map(perm => (
                      <PermissionBadge
                        key={perm.id}
                        permission={perm}
                        variant='secondary'
                        onRemove={() => removePermission(perm.id)}
                      />
                    ))}
                  </div>
                </TooltipProvider>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          {!isReadOnly && (
            <Button
              variant='purple'
              onClick={handleUpdate}
              disabled={!isFormValid || isSaving}
            >
              {isSaving ? 'Saving...' : 'Update'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditRoleDialog;
